import { useState, useEffect } from 'react';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useLinkBuildingPartners } from '@/hooks/useLinkBuildingPartners';
import { useLinkBuildingPartner } from '@/contexts/LinkBuildingPartnerContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DomainMultiSelect } from '@/components/DomainMultiSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';

export const GlobalAddPartnerDialog = () => {
  const { currentBrandId } = useBrandManager();
  const { createPartner, updatePartner } = useLinkBuildingPartners(currentBrandId || '');
  const { isOpen, editingPartner, closePartnerDialog } = useLinkBuildingPartner();
  
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    organization: '',
    contact_info: '',
    available_domains: [] as string[],
    notes: '',
    preferred_communication_channel: '' as 'slack' | 'email' | 'whatsapp' | 'linkedin' | '',
  });

  useEffect(() => {
    if (editingPartner) {
      setFormData({
        contact_name: editingPartner.contact_name,
        email: editingPartner.email || '',
        organization: editingPartner.organization || '',
        contact_info: editingPartner.contact_info || '',
        available_domains: editingPartner.available_domains || [],
        notes: editingPartner.notes || '',
        preferred_communication_channel: editingPartner.preferred_communication_channel || '',
      });
    } else {
      setFormData({
        contact_name: '',
        email: '',
        organization: '',
        contact_info: '',
        available_domains: [],
        notes: '',
        preferred_communication_channel: '',
      });
    }
  }, [editingPartner, isOpen]);

  const handleSubmit = () => {
    if (!currentBrandId) return;

    const payload = {
      ...formData,
      preferred_communication_channel: formData.preferred_communication_channel || undefined,
      brand_id: currentBrandId,
      user_id: currentBrandId,
    };

    if (editingPartner) {
      const updatePayload = {
        id: editingPartner.id,
        ...formData,
        preferred_communication_channel: formData.preferred_communication_channel || undefined,
      };
      updatePartner.mutate(updatePayload as any);
    } else {
      createPartner.mutate(payload as any);
    }
    closePartnerDialog();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closePartnerDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={editingPartner ? "Edit Partner" : "Add Partner"}
            popupContext="/link-building/partners"
            isOpen={isOpen}
          >
            {editingPartner ? 'Edit Partner' : 'Add Partner'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name *</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder="Company name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_info">Contact Info</Label>
              <Input
                id="contact_info"
                value={formData.contact_info}
                onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                placeholder="Phone, social media, etc."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="preferred_communication_channel">Preferred Communication Channel</Label>
              <Select
                value={formData.preferred_communication_channel}
                onValueChange={(value) => setFormData({ ...formData, preferred_communication_channel: value as 'slack' | 'email' | 'whatsapp' | 'linkedin' })}
              >
                <SelectTrigger id="preferred_communication_channel">
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slack">Slack</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"></div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="available_domains">Available Domains</Label>
            <DomainMultiSelect
              brandId={currentBrandId || ''}
              selectedDomainIds={formData.available_domains}
              onChange={(domainIds) => setFormData({ ...formData, available_domains: domainIds })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Internal notes about this partner..."
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closePartnerDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.contact_name}
          >
            {editingPartner ? 'Update' : 'Create'} Partner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
